import { adminApi, withSession } from '@/lib/api-client';
import { useEffect, useState } from 'react';
import type { ConversionSummaryDto, IntegrationStateDto } from '@ecom/contracts';
import { useIntegrationsStore } from '@/stores/integrations.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { count } from '@/lib/format';
import { ExternalIcon, PlugIcon, SearchIcon } from '@/components/icons';
import { Sheet, Tabs } from '@/components/page';
import { Alert, Button, Card, EmptyState, Field, Input, Pill, Skeleton, Switch, cx } from '@/components/ui';
import { SettingsPage } from './settings';

type Category = IntegrationStateDto['category'];
const CATEGORIES: Category[] = ['analytics', 'messaging', 'shipping', 'marketing', 'capability'];

/** Each category's tint, so a grid of services reads by kind before by name. */
const TINTS: Record<Category, string> = {
  analytics: 'bg-accent-sky text-accent-sky-ink',
  messaging: 'bg-accent-mint text-accent-mint-ink',
  shipping: 'bg-accent-amber text-accent-amber-ink',
  marketing: 'bg-accent-violet text-accent-violet-ink',
  capability: 'bg-surface-sunken text-ink-muted',
};

const categoryKey = (category: Category) => `integrationCategory${category.charAt(0).toUpperCase()}${category.slice(1)}` as MessageKey;

/**
 * Everything else a store plugs in (ADR-029), laid out like an app store:
 * tabs by what a service is for — "which pixel do we have" and "which courier
 * do we use" are asked by different people on different days — a card per
 * service with where it stands, and its keys in a panel beside the grid.
 *
 * An integration nothing in the platform acts on yet is shown and **cannot be
 * switched on**: a screen that accepts a courier key and never books a
 * shipment is worse than one that admits it is not built.
 */
export function IntegrationsPage() {
  const { integrations, loading, error, load } = useIntegrationsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [tab, setTab] = useState<Category | 'all'>('all');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  const present = CATEGORIES.filter((category) => integrations.some((entry) => entry.category === category));
  const needle = query.trim().toLowerCase();
  // The registry is a short list declared in code, not a table — filtering it here is fine.
  const shown = integrations.filter(
    (entry) =>
      (tab === 'all' || entry.category === tab) &&
      (!needle || `${entry.name} ${entry.summary} ${entry.key}`.toLowerCase().includes(needle)),
  );
  const selected = integrations.find((entry) => entry.key === open) ?? null;

  return (
    <SettingsPage
      title="integrations"
      subtitle="integrationsSubtitle"
      tabs={
        <Tabs
          label={t('integrations')}
          value={tab}
          onChange={setTab}
          items={[
            { id: 'all', label: t('integrationsAll'), count: count(integrations.length, locale) },
            ...present.map((category) => ({
              id: category,
              label: t(categoryKey(category)),
              count: count(integrations.filter((entry) => entry.category === category).length, locale),
            })),
          ]}
        />
      }
    >
      <div className="relative max-w-sm">
        <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-faint">
          <SearchIcon className="size-4" />
        </span>
        <Input
          type="search"
          value={query}
          placeholder={t('searchIntegrations')}
          aria-label={t('searchIntegrations')}
          onChange={(event) => setQuery(event.target.value)}
          className="ps-9"
        />
      </div>

      {error && !selected ? <Alert>{error}</Alert> : null}

      {tab === 'all' || tab === 'analytics' ? <ServerReporting /> : null}

      {loading && integrations.length === 0 ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-40" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <Card className="flex flex-1 flex-col">
          <EmptyState title={t('integrationsNone')} icon={<PlugIcon />} />
        </Card>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2">
          {shown.map((integration) => (
            <li key={integration.key}>
              <IntegrationCard integration={integration} onOpen={() => setOpen(integration.key)} />
            </li>
          ))}
        </ul>
      )}

      {selected ? <IntegrationSheet integration={selected} onClose={() => setOpen(null)} /> : null}
    </SettingsPage>
  );
}

/**
 * Purchases reported from the server (ADR-100): which pixels also report
 * from the server — their own field filled — and how the last week went.
 */
function ServerReporting() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const [summary, setSummary] = useState<ConversionSummaryDto[] | null>(null);

  useEffect(() => {
    withSession(() => adminApi.conversions.summary())
      .then((page) => setSummary(page.items))
      .catch(() => setSummary([]));
  }, []);

  if (!summary || summary.length === 0) return null;
  return (
    <Card className="flex flex-col gap-3 p-5">
      <div>
        <h2 className="text-base font-semibold text-ink">{t('conversionsTitle')}</h2>
        <p className="mt-0.5 text-sm text-ink-muted">{t('conversionsHint')}</p>
      </div>
      <ul className="grid gap-2 sm:grid-cols-2">
        {summary.map((entry) => (
          <li key={entry.platform} className="flex flex-col gap-1 rounded-control border border-line p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-ink">{t(`conversionPlatform_${entry.platform}`)}</span>
              <Pill tone={entry.on ? 'success' : 'neutral'}>{entry.on ? t('conversionsOn') : t('conversionsOff')}</Pill>
            </div>
            {entry.on || entry.sent + entry.failed > 0 ? (
              <span className="text-xs text-ink-muted">{t('conversionsWeek', { sent: count(entry.sent, locale), failed: count(entry.failed, locale) })}</span>
            ) : null}
            {entry.lastError ? (
              <span dir="ltr" className="text-start text-xs text-danger">
                {entry.lastError}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Where a service stands, in one word. */
function StatusPill({ integration }: { integration: IntegrationStateDto }) {
  const t = useT();
  if (!integration.implemented) return <Pill>{t('notShippedYet')}</Pill>;
  // Not switched on is not a problem to flag; the missing keys are named inside.
  return integration.enabled ? (
    <Pill tone="success">{t('integrationConnected')}</Pill>
  ) : (
    <Pill tone="neutral">{t('integrationNotConnected')}</Pill>
  );
}

function IntegrationCard({ integration, onOpen }: { integration: IntegrationStateDto; onOpen: () => void }) {
  const t = useT();

  return (
    <Card className={cx('flex h-full flex-col p-5', !integration.implemented && 'bg-surface/60')}>
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={cx(
            'inline-flex size-11 shrink-0 items-center justify-center rounded-control text-base font-bold',
            TINTS[integration.category],
          )}
        >
          {integration.name.slice(0, 1)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-ink">{integration.name}</p>
          <p className="text-xs text-ink-muted">{t(categoryKey(integration.category))}</p>
        </div>
        <StatusPill integration={integration} />
      </div>
      <p className="mt-3 line-clamp-2 flex-1 text-sm text-ink-muted">{integration.summary}</p>
      <div className="mt-4 flex items-center justify-between gap-3 border-t border-line pt-3">
        {integration.docsUrl ? (
          <a
            href={integration.docsUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink"
          >
            {t('integrationDocs')}
            <ExternalIcon className="size-3.5" />
          </a>
        ) : (
          <span />
        )}
        <Button variant="secondary" size="sm" onClick={onOpen}>
          {t('configure')}
        </Button>
      </div>
    </Card>
  );
}

/**
 * One service's switch and keys. A secret is written, never read back: the
 * box shows what is stored as a masked preview and stays empty, and an empty
 * box keeps the stored value.
 */
function IntegrationSheet({ integration, onClose }: { integration: IntegrationStateDto; onClose: () => void }) {
  const { saving, error, update } = useIntegrationsStore();
  const t = useT();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const dirty = Object.values(draft).some((value) => value !== '');

  return (
    <Sheet
      open
      title={integration.name}
      subtitle={t(categoryKey(integration.category))}
      onClose={onClose}
      footer={
        integration.fields.length > 0 ? (
          <>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              {t('close')}
            </Button>
            <Button
              disabled={saving || !dirty}
              onClick={async () => {
                if (await update(integration.key, { values: draft })) setDraft({});
              }}
            >
              {saving ? t('saving') : t('saveKeys')}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm text-ink-muted">{integration.summary}</p>
          <StatusPill integration={integration} />
        </div>

        {error ? <Alert>{error}</Alert> : null}

        <Card className="flex items-start justify-between gap-4 p-4">
          <div>
            <p className="text-sm font-medium text-ink">{t('integrationSwitch')}</p>
            <p className="mt-0.5 text-xs text-ink-muted">
              {integration.implemented ? t('integrationSwitchHint') : t('integrationUnbuiltHint')}
            </p>
          </div>
          <Switch
            label={t('integrationSwitch')}
            checked={integration.enabled}
            disabled={saving || !integration.implemented}
            onChange={(enabled) => void update(integration.key, { enabled })}
          />
        </Card>

        {integration.missingFields.length > 0 && integration.implemented ? (
          <Alert>
            {t('missingCredentials')}: <span dir="ltr">{integration.missingFields.join(', ')}</span>
          </Alert>
        ) : null}

        {integration.fields.length > 0 ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm font-semibold text-ink">{t('integrationKeys')}</p>
            {integration.fields.map((field) => (
              <Field
                key={field.key}
                label={field.key}
                htmlFor={`${integration.key}-${field.key}`}
                hint={
                  field.hasValue
                    ? `${t('stored')}: ${field.preview ?? '••••'}`
                    : (field.hint ?? (field.required ? t('required') : t('optional')))
                }
              >
                <Input
                  id={`${integration.key}-${field.key}`}
                  dir="ltr"
                  type={field.secret ? 'password' : 'text'}
                  autoComplete="off"
                  placeholder={field.hasValue ? t('leaveBlankToKeep') : ''}
                  value={draft[field.key] ?? ''}
                  onChange={(event) => setDraft((current) => ({ ...current, [field.key]: event.target.value }))}
                />
              </Field>
            ))}
          </div>
        ) : null}

        {integration.docsUrl ? (
          <a
            href={integration.docsUrl}
            target="_blank"
            rel="noreferrer"
            dir="ltr"
            className="inline-flex items-center gap-1 self-start text-xs text-ink-muted underline hover:text-ink"
          >
            {integration.docsUrl}
            <ExternalIcon className="size-3.5" />
          </a>
        ) : null}
      </div>
    </Sheet>
  );
}
